import { useEffect, useRef, useState } from 'react'

interface TypewriterOptions {
  /** Average ms per character. */
  speed?: number
  /** Delay before the first character appears. */
  startDelay?: number
  /** When false the effect resets and holds at zero characters. */
  active?: boolean
}

/**
 * Human-feeling typing animation: slight per-character jitter plus short
 * pauses after punctuation so the text never reads as a machine ticker.
 */
export function useTypewriter(text: string, options: TypewriterOptions = {}) {
  const { speed = 38, startDelay = 500, active = true } = options
  const [count, setCount] = useState(0)
  const timerRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    setCount(0)
    if (!active) return

    let index = 0
    const tick = () => {
      index += 1
      setCount(index)
      if (index >= text.length) return
      const previous = text[index - 1]
      const jitter = speed * (0.65 + Math.random() * 0.75)
      const pause =
        previous === ',' ? 200 : previous === '.' ? 260 : previous === ' ' ? 24 : 0
      timerRef.current = window.setTimeout(tick, jitter + pause)
    }

    const start = window.setTimeout(tick, startDelay)
    return () => {
      window.clearTimeout(start)
      if (timerRef.current) window.clearTimeout(timerRef.current)
    }
  }, [text, active, speed, startDelay])

  return {
    typed: text.slice(0, count),
    started: count > 0,
    done: count >= text.length,
  }
}
